import dayjs from 'dayjs';
import { DEVICE_TYPE, MZ_DATA } from 'src/views/data/gps-dashboard/constants';
import ImageHolders from './ImageHolders';
import TextConstants from './TextConstants';

/**
 * @description Get the data type of channel by input is the type number
 * @param {string} type
 * @returns {string} Channel data type
 */
const getChannelDataType = (type) => {
  switch (parseInt(type)) {
    case 0:
      return 'Number';
    case 1:
      return 'Boolean';
    case 2:
      return 'String';
    case 3:
      return 'Binary Data';
    case 4:
      return 'Location Data';
    default:
      return 'Unknown';
  }
};

const getFullName = (user) => {
  let firstName = user.firstName ? user.firstName : '';
  let lastName = user.lastName ? user.lastName : '';
  return `${firstName} ${lastName}`.trim() || 'ー';
};

const ROLE_CST = TextConstants.Common.RolesConst;

// Routes reachable by every logged-in role regardless of accessiblePaths
// (e.g. legal pages). They're a fallback-for-all, not a real "home" screen,
// so they must never be picked as a role's default landing page.
export const ACCESIBLE_FOR_ALL = ['/terms-of-service'];

// Shared by TheContent's route filter and getDefaultPagePath below: is
// `routePath` covered by one of the allowed path prefixes?
export const pathAllowed = (routePath, allowedList) =>
  !!routePath &&
  (allowedList || []).some(
    (item) => routePath === item || routePath.startsWith(item + '/')
  );

// Resolve the first screen a role can actually render, so a post-login or
// "/" redirect never lands a user on a route their role can't open (blank
// page). `routes` is the app route list (see src/routes.js), passed in by
// the caller to avoid a circular import.
export const getDefaultPagePath = (role, accessiblePaths, routes) => {
  if (role === ROLE_CST.SUPER_ADMIN) {
    return '/users';
  }

  const allowed = [...(accessiblePaths || []), ...ACCESIBLE_FOR_ALL];

  const canRender = (route) =>
    route.component &&
    route.roles?.includes(role) &&
    !ACCESIBLE_FOR_ALL.includes(route.path) &&
    pathAllowed(route.path, allowed);

  // Prefer the loggers screen as the default landing whenever the user can
  // open it, regardless of its position in `routes` (other screens such as
  // /api-keys, /stores, /receivers precede it in the list). Falls back to the
  // first route the role can render so a user without loggers access never
  // lands on a blank page.
  if ((routes || []).some((route) => route.path === '/loggers' && canRender(route))) {
    return '/loggers';
  }

  const landingRoute = (routes || []).find(canRender);

  return landingRoute?.path || '/login';
};

export const getBatteryImage = (percentage) => {
  let imageToDisplay = ImageHolders.Battery100;
  if (percentage >= 90) {
    imageToDisplay = ImageHolders.Battery100;
  } else if (percentage >= 70 && percentage < 90) {
    imageToDisplay = ImageHolders.Battery80;
  } else if (percentage >= 50 && percentage < 70) {
    imageToDisplay = ImageHolders.Battery60;
  } else if (percentage >= 30 && percentage < 50) {
    imageToDisplay = ImageHolders.Battery40;
  } else if (percentage >= 10 && percentage < 30) {
    imageToDisplay = ImageHolders.Battery20;
  } else if (percentage < 10) {
    imageToDisplay = ImageHolders.Battery0;
  }

  return imageToDisplay;
};

const downloadJsonFileFromObject = (fileName, Obj) => {
  const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
    JSON.stringify(Obj)
  )}`;
  const link = document.createElement('a');
  link.href = jsonString;
  link.download = fileName;

  link.click();
};

const getRandomArbitrary = (min, max) => {
  return Math.random() * (max - min) + min;
};

function getRandomColor() {
  // Generate a random hex color code
  var letters = '0123456789ABCDEF';
  var color = '#';
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

const getRandomHEXColors = (numColors) => {
  let strongColors = [];
  for (let i = 0; i < numColors; i++) {
    strongColors.push(getRandomColor());
  }
  return strongColors;
};

const isAbnormalStatus = (device) => {
  if (device.type === DEVICE_TYPE.LOGGER) {
    // .alarmFlag> 0, <=3
    // .asi > 0 and <=7
    // .mei == 1
    // .ode = 1
    return (
      (!isNaN(device.af1) && device.af1 !== MZ_DATA.E5_VALUE.NONE) ||
      (!isNaN(device.af2) && device.af2 !== MZ_DATA.E5_VALUE.NONE) ||
      (!isNaN(device.af3) && device.af3 !== MZ_DATA.E5_VALUE.NONE) ||
      (!isNaN(device.asi) && device.asi !== MZ_DATA.C2_VALUE.OK) ||
      (!isNaN(device.mei) && device.mei !== MZ_DATA.C3_VALUE.OK) ||
      (!isNaN(device.ode) && device.ode === MZ_DATA.C4_VALUE.YES)
    );
  } else {
    // Receiver error
    return !isNaN(device.rer) && device.rer !== MZ_DATA.O1_VALUE.NONE;
  }
};

const isBatteryWarning = (device) => {
  if (device.type === DEVICE_TYPE.LOGGER) {
    return !isNaN(device.bat) && device.bat <= MZ_DATA.C1_VALUE.ALARM_ISSUE;
  } else {
    // Receiver POWER  SUPPLY TYPE
    return (
      !isNaN(device.prs) &&
      [
        MZ_DATA.O3_VALUE.BUILT_IN_RECHARGEABLE_BATTERY_PW,
        MZ_DATA.O3_VALUE.AT_THE_START_OF_SHUTDOWN,
        MZ_DATA.O3_VALUE.BUILT_IN_RECHARGEABLE_BATTERY_NOT_CONNECTED,
      ].includes(device.prs)
    );
  }
};

const isOverPeriodActiveTimeRange = (
  periodActiveTimeRange,
  latestConnection
) => {
  if (!periodActiveTimeRange) {
    return false;
  }
  const latestConnectionUnix = new Date(latestConnection).getTime();
  return (
    latestConnectionUnix < periodActiveTimeRange.start ||
    latestConnectionUnix > periodActiveTimeRange.end
  );
};

const transformGatewayGPSData = (
  GatewayDataArr,
  periodActiveTimeRange,
  receiverCoordinatesSet
) => {
  return GatewayDataArr.map((gw) => {
    receiverCoordinatesSet.add(`${gw.crd?.[1]}_${gw.crd?.[2]}`);
    return {
      // id: `GWID_${gw.gatewayId}`,
      id: gw.gatewayId,
      name: gw.name,
      type: DEVICE_TYPE.RECEIVER,
      timestamp: gw.latestConnection,
      isAbnormalStatus: isAbnormalStatus(gw),
      isBatteryWarning: isBatteryWarning(gw),
      isOverPeriodActiveTimeRange: isOverPeriodActiveTimeRange(
        periodActiveTimeRange,
        gw.latestConnection
      ),
      lat: gw.crd?.[1],
      lng: gw.crd?.[2],
      rer: gw.rer,
      prs: gw.prs, // powerSupplyType
    };
  });
};

const transformLoggerGPSData = (
  LoggerDataArr,
  periodActiveTimeRange,
  receiverCoordinatesSet = new Set()
) => {
  const coordinatesDuplicatedNOs = {};
  const loggerArr = LoggerDataArr.map((lg) => {
    const latLngKey = `${lg.crd?.[1]}_${lg.crd?.[2]}`;
    if (receiverCoordinatesSet.has(latLngKey)) {
      coordinatesDuplicatedNOs[latLngKey] =
        (coordinatesDuplicatedNOs[latLngKey] || 0) + 1;
    }

    return {
      id: lg.deviceId,
      name: lg.name,
      type: DEVICE_TYPE.LOGGER,
      timestamp: lg.latestConnection,
      isAbnormalStatus: isAbnormalStatus(lg),
      isBatteryWarning: isBatteryWarning(lg),
      isOverPeriodActiveTimeRange: isOverPeriodActiveTimeRange(
        periodActiveTimeRange,
        lg.latestConnection
      ),
      lat: lg.crd?.[1],
      lng: lg.crd?.[2],
      evf: lg.evf,
      af1: lg.af1,
      af2: lg.af2,
      af3: lg.af3,
      asi: lg.asi,
      mei: lg.mei,
      ode: lg.ode,
      prs: lg.prs,
      bat: lg.bat, // battery level
      sty: lg.sty,
      ch1: lg.ch1,
      ch2: lg.ch2,
      ch3: lg.ch3,

      // The coordinates duplication flag between logger and receiver
      coordinatesDuplicatedNo: coordinatesDuplicatedNOs[latLngKey],
    };
  });
  loggerArr.sort((a, b) => {
    if (!isNaN(a.evf) && isNaN(b.evf)) {
      return -1;
    }
    if (!isNaN(b.evf) && isNaN(a.evf)) {
      return 1;
    }
  });
  return loggerArr;
};

const getSensorTypeNameByKey = (sensorKey) => {
  // 、0:内蔵サーミスタ、1:外付けサーミスタ、2:温湿度、3:測温抵抗体、4:電圧、5:パルス、6:K熱電対、7:T熱電対
  switch (sensorKey) {
    case 0:
      return '内蔵サーミスタ';
    case 1:
      return '外付けサーミスタ';
    case 2:
      return '温湿度';
    case 3:
      return '測温抵抗体';
    case 4:
      return '電圧';
    case 5:
      return 'パルス';
    case 6:
      return 'K熱電対';
    case 7:
      return 'T熱電対';

    default:
      return '--';
  }
};

export const chunkArray = (arr, chunkSize) => {
  const result = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize).flat());
  }
  return result;
};

export const fetchCombinedDataParallellyAsync = async (func, url) => {
  return func(url).then(async (files) => {
    const tasks = [];
    files.forEach((file) => {
      tasks.push(
        fetch(file)
          .then((response) => {
            if (response.status === 403) {
              throw new Error(TextConstants.Common.FileExpired);
            }
            if (!response.ok) {
              throw new Error(TextConstants.Common.Failed);
            }
            return response.json();
          })
          .catch((error) => {
            throw error;
          })
      );
    });
    return await Promise.all(tasks).then((responses) => responses.flat());
  });
};

export const fetchDataParallellyAsync = async (
  asyncFunc,
  url,
  start,
  end,
  step = 30,
  params = {}
) => {
  const tasks = [];

  let current = dayjs(start);
  while (current.valueOf() <= end.valueOf()) {
    let nextStep = dayjs(current).add(step, 'minutes');

    let isLastStep = false;
    if (nextStep.valueOf() >= end.valueOf()) {
      nextStep = dayjs(end);
      isLastStep = true;
    }

    let startTime = new Date(current);
    startTime = dayjs(startTime.setUTCHours(startTime.getUTCHours() + 9))
      .toISOString()
      .replace(/\.\d+Z$/, '+09:00');

    let endTime = new Date(nextStep);
    endTime = dayjs(endTime.setUTCHours(endTime.getUTCHours() + 9))
      .add(isLastStep ? 0 : -1, 'seconds')
      .toISOString()
      .replace(/\.\d+Z$/, '+09:00');

    const replacedUrl = url
      .replace('$start$', startTime)
      .replace('$end$', endTime);

    tasks.push(asyncFunc(replacedUrl, params));

    current = dayjs(current).add(step, 'minutes');
  }

  return await Promise.all(tasks).then((responses) => responses.flat());
};

const generateTimestamps = (startTime, endTime, intervalInSeconds) => {
  const result = {};
  const intervalInMillis = intervalInSeconds * 1000; // Convert seconds to milliseconds

  for (
    let current = startTime;
    current <= endTime;
    current += intervalInMillis
  ) {
    result[current] = null;
  }

  return result;
};

export const mapIntervalPoints = (startTime, endTime, interval, data) => {
  let startDatetime = new Date(startTime);
  let endDatetime = new Date(endTime);
  let startISOStr = dayjs(
    startDatetime.setUTCHours(startDatetime.getUTCHours() + 9)
  )
    .toISOString()
    .replace(/\.\d+Z$/, '+09:00');
  let endISOStr = dayjs(endDatetime.setUTCHours(endDatetime.getUTCHours() + 9))
    .toISOString()
    .replace(/\.\d+Z$/, '+09:00');

  const timestamps = generateTimestamps(
    dayjs(startISOStr).valueOf(),
    dayjs(endISOStr).valueOf(),
    interval
  );

  const result = {};

  // Convert each data point's `lts` to a timestamp in milliseconds for comparison
  const dataWithTimestamps = data.map((item) => ({
    ...item,
    ts: new Date(item.lts).getTime(),
    used: false, // Flag to track if the data point has been used
  }));

  const formatDate = (timestamp) => {
    return dayjs(timestamp).format('YYYY/MM/DD HH:mm:ss');
  };

  // For each timestamp key in the `timestamps` object
  Object.keys(timestamps).forEach((tsKey) => {
    const targetTime = parseInt(tsKey, 10);

    if (dataWithTimestamps.length === 0) {
      // No data available; set empty value with `its` in custom format
      result[tsKey] = { its: formatDate(targetTime) };
      return;
    }

    // Find the nearest unused point by calculating the absolute time difference
    const unusedDataPoints = dataWithTimestamps.filter((item) => !item.used);

    if (unusedDataPoints.length === 0) {
      // If no unused data points left, just set `its` to the formatted timestamp
      result[tsKey] = { its: formatDate(targetTime) };
      return;
    }

    const nearestPoint = unusedDataPoints.reduce((prev, curr) => {
      return Math.abs(curr.ts - targetTime) < Math.abs(prev.ts - targetTime)
        ? curr
        : prev;
    });

    if (nearestPoint) {
      // Mark this point as used
      nearestPoint.used = true;

      // Map the nearest point's data to the current timestamp key
      result[tsKey] = {
        ...nearestPoint,
        its: formatDate(targetTime),
      };
    } else {
      // If no nearest point found, just set `its` to the formatted timestamp
      result[tsKey] = { its: formatDate(targetTime) };
    }
  });

  return Object.values(result);
};

export const downsampleData = (points, maxPoints) => {
  if (!points?.length || points?.length < maxPoints) {
    return [];
  }

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  const start = dayjs(firstPoint.timestamp, 'YYYY/MM/DD HH:mm').valueOf();
  const end = dayjs(lastPoint.timestamp, 'YYYY/MM/DD HH:mm').valueOf();

  // Calculate w and ensure it's even
  let w = Math.floor(Math.floor((end - start) / 1000) / maxPoints);
  w = w % 2 === 0 ? w : w - 1; // Make w even by subtracting 1 if it's odd

  if (w <= 0) {
    throw new Error('Window size W must be greater than 0.');
  }

  const downsampledData = [];
  const batchMap = new Map();

  points.forEach((sample) => {
    const sampleTime = dayjs(sample.timestamp).valueOf(); // Convert ISO string to milliseconds
    const windowIndex = Math.floor((sampleTime - start) / (w * 1000));
    if (windowIndex >= 0) {
      if (!batchMap.has(windowIndex)) {
        batchMap.set(windowIndex, []);
      }
      batchMap.get(windowIndex).push(sample);
    }
  });

  for (const [windowIndex, buffer] of batchMap.entries()) {
    const currentTime = start + windowIndex * w * 1000; // Start time of the current window
    let midTime = currentTime + (w * 1000) / 2; // Midpoint time for the window

    // if midTime is more than endTime, then set midTime to endTime
    if (midTime > end) {
      midTime = end;
    }

    if (buffer.length > 0) {
      const values = buffer.map((sample) => sample.value);

      const maxValue = Math.max(...values);
      const minValue = !values.includes(null) ? Math.min(...values) : null;

      // Add max and min samples to the downsampled data
      downsampledData.push({
        timestamp: dayjs(midTime).format('YYYY/MM/DD HH:mm:ss'),
        value: maxValue,
      });
      downsampledData.push({
        timestamp: dayjs(midTime).format('YYYY/MM/DD HH:mm:ss'),
        value: minValue,
      });
    }
  }

  return { downsampledData, w };
};

export const isInAbnormalRange = (value) => {
  // Check for null or undefined
  if (value == null) {
    return false;
  }
  return -2100 <= value && value <= -2093;
};

export const roundMeasureValue = (dataUnit, value) => {
  switch (dataUnit) {
    case TextConstants.Common.SensorUnit.Temperature:
    case TextConstants.Common.SensorUnit.Humidity:
    case TextConstants.Common.SensorUnit.Pt100:
      return value.toRoundString(1);
    case TextConstants.Common.SensorUnit.Voltage:
      return value.toRoundString(3);
    case TextConstants.Common.SensorUnit.Pulse:
      return Math.round(value);
    default:
      return value;
  }
};

export default {
  getChannelDataType,
  getFullName,
  getDefaultPagePath,
  downloadJsonFileFromObject,
  getRandomArbitrary,
  getRandomHEXColors,
  transformGatewayGPSData,
  transformLoggerGPSData,
  getSensorTypeNameByKey,
};
